import moment from "moment";
import { i18n } from "i18next";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Theme, ThemeHistory, CheckInData, UtcTime } from 'shared/models';
import { ClickToCopy } from "ui/shared/containers";
import './CheckInDetails.css';
import { showEditor } from "actions/editorActions";
import { useDispatch } from "react-redux";

const reducedOpacity = {
    opacity: .75,
};
const reducedOpacityRed = {
    opacity: .75,
    color: '#C00',
};

export const Unknown = () => {
    const { t } = useTranslation();
    return (
        <span style={ reducedOpacity }>
            { t('DEVICE.STATUS.UNKNOWN') }
        </span>
    );
};

export const Never = () => {
    const { t } = useTranslation();
    return (
        <span style={ reducedOpacity }>
            { t('DEVICE.STATUS.NEVER') }
        </span>
    );
};

export const Loading = () =>
    <span style={ reducedOpacity }>...</span>
;

export const Late = () => {
    const { t } = useTranslation();
    return (
        <span style={ reducedOpacityRed }>
            { t('DEVICE.STATUS.LATE') }
        </span>
    );
};

const nullOrUndefinedOrMinus255 = (value: any) =>
    value === null || typeof value === 'undefined' || value === -255
;

export enum IconType {
    warning = 'warning',
    notFound = 'notFound'
}
export type Icon = {
    type: IconType;
    title: string;
};

const deviceThemeIcon = {
    [IconType.warning]: 'yellow warning circle icon',
    [IconType.notFound]: 'red remove circle icon',
};

export const Stat = (props: { isLoading: boolean; isTimeStat?: boolean; isTimeOutOfSync?: boolean; linkTo?: string; icon?: Icon ; data?: string | number | null | ReactNode; fallback?: ReactNode }) => {
    const dispatch = useDispatch();
    const handleThemeLinkClick = () => {
        dispatch(showEditor({ exitPath: window.location.pathname }));
    };
        return (
            <div className="stat-value">
                { props.isLoading && nullOrUndefinedOrMinus255(props.data)
                    ? <Loading/>
                    : nullOrUndefinedOrMinus255(props.data)
                        ? props.fallback || <Unknown/>
                        :
                        <>
                        {props.icon?.type ?
                            <i className={ deviceThemeIcon[props.icon?.type] } title={ props.icon?.title }/>
                            :
                            undefined
                        }
                        {props.linkTo ?
                            <Link
                                className="_name"
                                to={ props.linkTo }
                                onClick={ handleThemeLinkClick }
                            >
                                { props.data }
                            </Link>
                            : props.data
                        }
                        </>
                }
                { props.data && props.isTimeStat && props.isTimeOutOfSync &&
                    <i className="warning sign icon red _time-out-of-sync"/>
                }
            </div>
        );
    };

const StartupAt = ({ time, count, timeZone }: { time: number; count: number; timeZone: string }) => {
    const { i18n } = useTranslation();
    return time ? (
        <span style={ { color: isSuspiciousStartupTime(time, timeZone) ? '#C00' : undefined } }>
            #{ count } - { moment.tz(time, timeZone).locale(i18n.language).calendar() }
        </span>
    ) : <Unknown/>;
};

export type Props = {
    isLoading: boolean;
    timeZone: string;
    deviceServerTimeDelta: number;
    isTimeOutOfSync: boolean;
    checkInData: CheckInData | null;
    lastCheckIn: UtcTime | null;
    nextCheckIn: UtcTime | null;
    theme: Theme | null;
    themeUrl: string;
    themeHistory: ThemeHistory | null;
};

export const CheckInDetails = ({
    isLoading,
    timeZone,
    deviceServerTimeDelta,
    isTimeOutOfSync,
    checkInData,
    lastCheckIn,
    nextCheckIn,
    theme,
    themeUrl,
    themeHistory,
}: Props) => {
    const { t, i18n } = useTranslation();

    let icon: Icon | undefined;
    let isMosaicOneTheme = true;
    // 2018-09-12: Matt started putting the eTag of the theme in the name and wants me
    //             to do a lookup. Could do something similar for Matisse
    let eTag = null;
    let currentTheme = checkInData?.CurrentTheme;
    const ttgETagRegex = /^MosaicONE\[(.*?)\]$/i;
    const m = currentTheme && ttgETagRegex.exec(currentTheme);
    if (m) {
        eTag = m[1];
        currentTheme = eTag;
    }
    else {
        // Test to see if the entire 'currentTheme' is an eTag
        eTag = currentTheme;
        isMosaicOneTheme = false;
    }

    const themeName = theme?.displayName || '';
    const latestThemeEtag = theme?.etag;

    if (themeName) {
        currentTheme = currentTheme?.replace(eTag!, themeName);
    }
    
    let isUnknownTheme;
    if (!themeHistory?.themeId || !latestThemeEtag) {
        icon = {
            type: IconType.notFound,
            title: t('ERROR.NOT_FOUND'),
        };
        isUnknownTheme = true;
    }
    else if (latestThemeEtag && eTag?.replace(/"/g, '') !== latestThemeEtag?.replace(/"/g, '')) {
        icon = {
            type: IconType.warning,
            title: t('DEVICE.WARNING.OLD_THEME_VERSION'),
        };
    }
    
    const adjustedLastUsed = checkInData ? (checkInData.LastUsedAt + deviceServerTimeDelta) : null;
    const adjustedStartupTime = checkInData ? (checkInData.StartupTime + deviceServerTimeDelta) : null;

    return (
        <div id="check-in-details">
            <div className="stat-name">{ t('DEVICE.STATUS.LAST_CHECK_IN') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.NEXT_CHECK_IN') }</div>
            <div></div>
            <Stat
                isLoading={ isLoading }
                isTimeStat={ true }
                isTimeOutOfSync={ isTimeOutOfSync }
                data={ lastCheckIn && moment.tz(lastCheckIn, timeZone).locale(i18n.language).fromNow() }
                fallback={ <Never/> }
            />
            <Stat
                isLoading={ isLoading }
                data={
                    lastCheckIn && (nextCheckIn && nextCheckIn >= Date.now()
                        ? moment.tz(nextCheckIn, timeZone).locale(i18n.language).fromNow()
                        : <Late/>)
                }
            />
            <div></div>

            <div className="stat-name">{ t('DEVICE.STATUS.CURRENT_THEME') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.SOFTWARE_VERSION') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.ELEVATOR_NAME') }</div>
            <Stat isLoading={ isLoading } linkTo={ isMosaicOneTheme && !isUnknownTheme ? themeUrl : '' } icon={ icon } data={ currentTheme } />
            <Stat isLoading={ isLoading } data={ checkInData?.SoftwareVersion }/>
            <ClickToCopy>
                <Stat isLoading={ isLoading } data={ checkInData?.ElevatorName }/>
            </ClickToCopy>

            <div className="stat-name">{ t('DEVICE.STATUS.IP_ADDRESS') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.LAST_USED') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.DISPLAY_RESOLUTION') }</div>
            <ClickToCopy>
                <Stat isLoading={ isLoading } data={ checkInData?.IP }/>
            </ClickToCopy>
            <Stat isTimeStat={ true } isLoading={ isLoading } data={ formatLastUsed(adjustedLastUsed, timeZone, i18n) }/>
            <Stat isLoading={ isLoading } data={ checkInData && `${checkInData.DisplayWidth} x ${checkInData.DisplayHeight}` }/>

            <div className="stat-name">{ t('DEVICE.STATUS.MAC_ADDRESS') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.STARTUP') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.LCD_POWER_SAVING_MODE') }</div>
            <ClickToCopy>
                <Stat isLoading={ isLoading } data={ checkInData?.MAC }/>
            </ClickToCopy>
            <Stat
                isLoading={ isLoading }
                isTimeStat={ true }
                isTimeOutOfSync={ isTimeOutOfSync }
                data={ adjustedStartupTime &&
                    <StartupAt count={ checkInData?.StartupCount || 0 } time={ adjustedStartupTime! } timeZone={ timeZone }/>
                }
            />
            <Stat isLoading={ isLoading } data={ checkInData?.LCDPowerSavingMode }/>

            <div className="stat-name">{ t('DEVICE.STATUS.POWER_CYCLES') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.POWER_ON_TIME') }</div>
            <div className="stat-name">{ t('DEVICE.STATUS.UNEXPECTED_SHUTDOWNS') }</div>
            <Stat isLoading={ isLoading } data={ checkInData?.PowerCycleCount }/>
            <Stat isLoading={ isLoading }
                isTimeStat={ true }
                isTimeOutOfSync={ isTimeOutOfSync }
                data={ typeof checkInData?.PowerOnHours === 'number' && checkInData?.PowerOnHours >= 0
                    ? moment.duration(checkInData?.PowerOnHours, 'h').locale(i18n.language).humanize()
                    : null }
            />
            <Stat isLoading={ isLoading } data={ checkInData?.UnexpectedShutdownCount }/>
        </div>
    );
};

function formatLastUsed(date: number | null | undefined, timeZone: string, i18n: i18n) {
    if (typeof date !== 'number') {
        return null;
    }
    return date && date > 946684800000 // Jan 1, 2000
        ? moment.tz(date, timeZone).locale(i18n.language).fromNow()
        : i18n.t('DEVICE.STATUS.NEVER');
}

// Matt wants any startup times outside of 2:00am and 4:30am (local time) to be highlighted
// any startup times outside of 2:55am and 3:05am (italy time) should also be highlighted
export function isSuspiciousStartupTime(time: number, timeZone: string) {
    const momentLocalTime = moment.tz(time, timeZone);
    const momentItalyTime = moment.tz(time, 'Europe/Rome');
    const localEarlyBound = momentLocalTime.clone().set({ hour: 2, minute: 0, second: 0, millisecond: 0 });
    const localLateBound = momentLocalTime.clone().set({ hour: 4, minute: 30, second: 0, millisecond: 0 });
    const italyEarlyBound = momentItalyTime.clone().set({ hour: 2, minute: 55, second: 0, millisecond: 0 });
    const italyLateBound = momentItalyTime.clone().set({ hour: 3, minute: 5, second: 0, millisecond: 0 });
    return !(momentLocalTime.isBetween(localEarlyBound, localLateBound) || momentItalyTime.isBetween(italyEarlyBound, italyLateBound));
}
